import { useState, useCallback, useEffect } from 'react';

interface CachedResponse {
  data: any;
  timestamp: number;
  url: string;
}

export const useOfflineStorage = () => {
  const [cachedData, setCachedData] = useState<Record<string, CachedResponse>>({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem('offlineApiCache');
      if (stored) {
        setCachedData(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading offline cache:', error);
      setCachedData({});
    }
  }, []);

  const saveToCache = useCallback((url: string, data: any) => {
    const cacheEntry: CachedResponse = {
      data,
      timestamp: Date.now(),
      url
    };
    
    setCachedData(prevCache => {
      const newCache = {
        ...prevCache,
        [url]: cacheEntry
      };
      
      localStorage.setItem('offlineApiCache', JSON.stringify(newCache));
      console.log(`💾 Cached data for: ${url}. Total entries: ${Object.keys(newCache).length}`);
      
      return newCache;
    });
  }, []);

  const getFromCache = useCallback((url: string): any | null => {
    const cached = cachedData[url];
    if (cached) {
      console.log(`📤 Serving from cache: ${url}`);
      return cached.data;
    }
    console.log(`❌ No cache found for: ${url}`);
    return null;
  }, [cachedData]);

  const clearCache = useCallback(() => {
    setCachedData({});
    localStorage.removeItem('offlineApiCache');
    console.log('🗑️ Offline cache cleared');
  }, []);

  const getCacheInfo = useCallback(() => {
    try {
      const stored = localStorage.getItem('offlineApiCache');
      const parsed: Record<string, CachedResponse> = stored
        ? JSON.parse(stored)
        : cachedData;

      const urls = Object.keys(parsed || {});
      const totalSize = JSON.stringify(parsed || {}).length;

      return {
        cachedUrls: urls,
        totalEntries: urls.length,
        estimatedSize: `${(totalSize / 1024).toFixed(2)} KB`
      };
    } catch (error) {
      console.error('Error reading offline cache info:', error);
      const urls = Object.keys(cachedData);
      const totalSize = JSON.stringify(cachedData).length;
      return {
        cachedUrls: urls,
        totalEntries: urls.length,
        estimatedSize: `${(totalSize / 1024).toFixed(2)} KB`
      };
    }
  }, [cachedData]);

  return {
    saveToCache,
    getFromCache,
    clearCache,
    getCacheInfo,
    cachedData
  };
};
